import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { useSeo } from "@/hooks/useSeo";
import { contentPrinciples, siteName, siteSlogan, siteStats, testimonials } from "@/mocks/news";

const aboutJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于沸点爆料",
    inLanguage: "zh-CN",
  },
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "沸点爆料",
    description: "聚合娱乐爆料、社会热点与科技快讯的中文内容门户。",
    email: "tip@feidian-kuaixun.example",
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页" },
      { "@type": "ListItem", position: 2, name: "关于我们" },
    ],
  },
];

export default function AboutPage() {
  useSeo({
    title: "关于我们_沸点爆料的定位与内容原则 - 沸点爆料",
    description:
      "沸点爆料是一个追踪刚刚发生事件的中文内容门户，坚持先核实再发布、保护当事人隐私，并持续更新热点后续进展。了解我们的定位、理念与内容原则。",
    keywords: "关于沸点爆料,热点爆料,内容原则,热点爆料今日,网站定位",
    path: "/about",
    ogType: "website",
    jsonLd: aboutJsonLd,
  });

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />

      <main className="flex-1 w-full">
        {/* 品牌首屏 */}
        <section className="w-full bg-background-100 border-b border-background-200">
          <div className="w-full px-4 md:px-6 py-8 md:py-14">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            <div className="mt-5 max-w-3xl">
              <span className="inline-flex items-center gap-2 text-xs font-medium tracking-widest text-primary-600 mb-3">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-fire-line"></i>
                </span>
                品牌介绍
              </span>
              <h1 className="font-heading text-3xl md:text-5xl font-black text-foreground-950 tracking-tight leading-tight">
                关于 {siteName}
              </h1>
              <p className="mt-4 text-base md:text-lg text-foreground-600 leading-relaxed">
                {siteSlogan}。我们是一个专注热点爆料与今日快讯的中文内容门户，把散落在各处的热门事件整理成一条清晰的时间线，让你用更短的时间看清当日发生了什么。
              </p>
            </div>

            <div className="mt-8 md:mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {siteStats.map((stat) => (
                <div key={stat.label} className="rounded-lg bg-background-50 border border-background-200 p-4 md:p-5">
                  <p className="font-heading text-2xl md:text-3xl font-black text-primary-600">{stat.value}</p>
                  <p className="mt-1.5 text-xs md:text-sm text-foreground-600">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="w-full px-4 md:px-6 py-10 md:py-16 flex flex-col gap-14 md:gap-20">
          {/* 定位与理念 */}
          <section aria-labelledby="position-title" className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <div className="lg:col-span-5">
              <h2 id="position-title" className="font-heading text-2xl md:text-3xl font-black text-foreground-950 tracking-tight">
                我们想做什么
              </h2>
            </div>
            <div className="lg:col-span-7 flex flex-col gap-5">
              <p className="text-base text-foreground-700 leading-8">
                每天都有大量信息在流动，但真正影响生活的热门事件，往往被淹没在碎片化的转发里。{siteName} 想做的，是把这些线索收拢起来，按时间顺序梳理清楚，让读者不必在十几个群聊之间来回翻找。
              </p>
              <p className="text-base text-foreground-700 leading-8">
                我们不追求「最快」，而是追求「更靠谱」。一条爆料从收到线索到发布，通常要经过来源确认与交叉核实两个环节；在信息尚不明朗时，我们宁可慢一步，也会明确标注「有待核实」。
              </p>
              <p className="text-base text-foreground-700 leading-8">
                热点不是发完就结束。对于关注度较高的内容，我们会持续跟进后续进展，并把更新补回到同一篇内容中，让事件的完整脉络可以被追溯。
              </p>
            </div>
          </section>

          {/* 内容原则 */}
          <section id="principles" aria-labelledby="principles-title" className="scroll-mt-24">
            <div className="mb-6 md:mb-8">
              <span className="inline-flex items-center gap-2 text-xs font-medium tracking-widest text-primary-600 mb-2">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-bookmark-line"></i>
                </span>
                内容原则
              </span>
              <h2 id="principles-title" className="font-heading text-2xl md:text-3xl font-black text-foreground-950 tracking-tight">
                我们坚持的四条底线
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
              {contentPrinciples.map((item, index) => (
                <div
                  key={item.title}
                  className={`rounded-lg border p-5 md:p-6 ${
                    index % 2 === 0 ? "bg-background-50 border-background-200" : "bg-accent-50 border-accent-200"
                  }`}
                >
                  <span
                    className={`w-11 h-11 flex items-center justify-center rounded-md text-2xl ${
                      index % 2 === 0 ? "bg-primary-100 text-primary-700" : "bg-accent-200 text-accent-900"
                    }`}
                  >
                    <i className={item.icon}></i>
                  </span>
                  <h3 className="mt-4 font-heading font-bold text-lg text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* 读者口碑 */}
          <section aria-labelledby="voice-title">
            <div className="mb-6 md:mb-8">
              <span className="inline-flex items-center gap-2 text-xs font-medium tracking-widest text-primary-600 mb-2">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-chat-quote-line"></i>
                </span>
                读者口碑
              </span>
              <h2 id="voice-title" className="font-heading text-2xl md:text-3xl font-black text-foreground-950 tracking-tight">
                读者怎么说
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {testimonials.map((t) => (
                <figure key={t.name} className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-6 flex flex-col">
                  <span className="w-8 h-8 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700 text-lg">
                    <i className="ri-double-quotes-l"></i>
                  </span>
                  <blockquote className="mt-4 text-sm text-foreground-700 leading-relaxed flex-1">{t.text}</blockquote>
                  <figcaption className="mt-4 pt-4 border-t border-background-200">
                    <p className="font-heading font-bold text-sm text-foreground-950">{t.name}</p>
                    <p className="text-xs text-foreground-500 mt-0.5">{t.role}</p>
                  </figcaption>
                </figure>
              ))}
            </div>
          </section>

          {/* 联系引导 */}
          <section className="rounded-lg bg-secondary-900 px-5 md:px-10 py-8 md:py-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-black text-background-50 tracking-tight">
                有线索，或者想聊聊合作？
              </h2>
              <p className="mt-3 text-sm text-background-200/90 leading-relaxed max-w-xl">
                线索接收全天 24 小时开放，涉及隐私的内容我们会做脱敏处理。商务与内容合作也欢迎通过同一入口联系。
              </p>
              <p className="mt-4 text-sm text-accent-300">邮箱：tip@feidian-kuaixun.example</p>
            </div>
            <Link
              to="/contact"
              className="shrink-0 inline-flex items-center justify-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-mail-send-line"></i>
              </span>
              前往联系页面
            </Link>
          </section>
        </div>
      </main>

      <Footer />
      <BackToTop />
    </div>
  );
}